import {ISimplePresentation, ISimplePresentationRunnerController} from "../SimplePresentationRunner";
import {Game} from "../Game";
import {gsap} from "gsap";
import {IMonster, IMonsterWalk, IPosition, MONSTER_EXPRESSION} from "../server/IData";
import {Symbol} from "../reels/Symbol";
import {MonsterSymbol} from "../reels/MonsterSymbol";
import {Sound} from "../sound/Sound";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

export class GrenadeRunner implements ISimplePresentation {

    public shouldRun(): boolean {
        return Game.model.gameData.grenadePosition && Game.model.gameData.grenadePosition.x !== -1 && !Game.model.gameData.suspendedAction;
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    controller.finish();
                }
            }),
            monsters: IMonster[] = Game.model.gameData.monsters,
            grenadeTarget: IMonsterWalk = Game.model.gameData.grenadePosition,
            targetRowId: number = grenadeTarget.y,
            reelsSymName: string = Game.model.gameData.evaluatedArea[grenadeTarget.x][targetRowId],
            grSymbol: Symbol = Game.reelsView.reelModels[grenadeTarget.x].getSymbolAt(targetRowId, reelsSymName),
            carriedSymbolUserNode: IPosition = Game.model.gameData.carriedSymbolUserNode,
            turnDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.2 / 2 : 0.2;

        tl.add(() => {
            controller.setSkipCallback(() => {
                this.onSkip(tl);
            });
        });

        for (let ctr: number = 0; ctr < monsters.length; ctr++) {
            const monster: IMonster = monsters[ctr];
            if (!monster.path || monster.path.length === 0) {
                continue;
            }

            if (monster.path[0].x === carriedSymbolUserNode.x && monster.path[0].y === carriedSymbolUserNode.y) {
                const monsterSymbol: MonsterSymbol = Game.reelsView.reelModels[monster.path[0].x].getMonsterSymbolAt(monster.symbol),
                    monsterName: string = monsterSymbol.name;
                let isMonsterExploding: boolean = false;

                tl.add([
                    () => {
                        if (monsterSymbol && monsterSymbol.parent) {
                            monsterSymbol.parent.addChild(monsterSymbol);
                        }
                    },
                    monsterSymbol.turnMonsterTowardsTarget(monster.path[0].x, grenadeTarget.x, turnDuration)
                ]);
                tl.add([
                    monsterSymbol.throwGrenade(grenadeTarget, targetRowId, grSymbol),
                    this.handleMonstersExpression(grenadeTarget, targetRowId)
                ]);

                if (Game.checkIfTriggeringMonsterIsPartOfExplosion(grenadeTarget.x - 1, targetRowId - 1, targetRowId + 2, monsterName)) {
                    isMonsterExploding = true;
                } else if (Game.checkIfTriggeringMonsterIsPartOfExplosion(grenadeTarget.x, targetRowId - 1, targetRowId + 2, monsterName)) {
                    isMonsterExploding = true;
                } else if (Game.checkIfTriggeringMonsterIsPartOfExplosion(grenadeTarget.x + 1, targetRowId - 1, targetRowId + 2, monsterName)) {
                    isMonsterExploding = true;
                }

                if (!isMonsterExploding) {
                    tl.add([
                        monsterSymbol.getStateAnimation(MONSTER_EXPRESSION.LAUGH, 6),
                        () => {
                            Game.soundPlayer.playSound(monsterSymbol.name + "Laugh" + MathHelper.randomIntInRange(1, 3), 1);
                        }
                    ]);
                }

                break;
            }
        }
    }

    private handleMonstersExpression(grenadeTarget: IMonsterWalk, targetRowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            monstersToExplode: string[] = [],
            monsters: IMonster[] = Game.model.gameData.monsters,
            carriedSymbolUserNode: IPosition = Game.model.gameData.carriedSymbolUserNode;

        let attackingMonster: MonsterSymbol = Game.reelsView.reelModels[carriedSymbolUserNode.x].getMonsterSymbolAtRowId(carriedSymbolUserNode.y);

        for (let ctr: number = 0; ctr < monsters.length; ctr++) {
            const monster: IMonster = monsters[ctr];
            if (!monster.path || monster.path.length === 0) {
                continue;
            }
            if (monster.path[0].x === carriedSymbolUserNode.x && monster.path[0].y === carriedSymbolUserNode.y) {
                const monsterSymbol: MonsterSymbol = Game.reelsView.reelModels[monster.path[0].x].getMonsterSymbolAt(monster.symbol);
                attackingMonster = monsterSymbol;
                break;
            }
        }

        Game.getMonstersToExplode(grenadeTarget.x - 1, targetRowId - 1, targetRowId + 2, monstersToExplode);
        Game.getMonstersToExplode(grenadeTarget.x, targetRowId - 1, targetRowId + 2, monstersToExplode);
        Game.getMonstersToExplode(grenadeTarget.x + 1, targetRowId - 1, targetRowId + 2, monstersToExplode);

        Game.updateSurprisedMonsters(monstersToExplode, attackingMonster);

        Game.monsterReelsView.monsterSymbolContainer.children.forEach((monster: PIXI.DisplayObject) => {
            if (monstersToExplode.includes(monster.name)) {
                tl.add([
                    (monster as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.SHOCK, 5),
                    () => {
                        if (monster.name !== attackingMonster.name){
                            Game.soundPlayer.playSound(monster.name + "Surprised" + MathHelper.randomIntInRange(1, 3), Game.getSurprisedSoundPlayVolume());
                        }
                    }
                ], 2.2);
            }
        });

        return tl;
    }

    private onSkip(tl: gsap.core.Timeline): void {
        tl.totalProgress(1);
        Game.soundPlayer.stopSound(Sound.GR_ACTIVATE);
        Game.soundPlayer.stopSound(Sound.GR_EXPLODE);
        Game.soundPlayer.fadeInAndStartSound({
            name: Sound.SNARE_PLAYER,
            from: 0,
            to: 0,
            duration: 0,
            repeat: Number.MAX_VALUE,
            isResume: true

        });

        // #388
        Game.monsterReelsView.monsterSymbolContainer.children.forEach((monsterSymbol: PIXI.DisplayObject) => {
            (monsterSymbol as MonsterSymbol).skip();
        });
    }
}